import { useState, useMemo } from "react";
import { KanbanCard, KanbanCardTag } from "@/types/database";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { MoreHorizontal, Search } from "lucide-react";
import { CardDetailsDialog } from "../CardDetailsDialog";

interface ListViewProps {
  boardId: string;
  columns: { id: string; name: string }[];
  cards: Array<KanbanCard & { column_name?: string }>;
  onRefreshCards: () => void;
}

export function ListView({ boardId, columns, cards, onRefreshCards }: ListViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCard, setSelectedCard] = useState<KanbanCard | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  
  // Add column name to each card for display
  const cardsWithColumnNames = useMemo(() => {
    return cards.map(card => {
      const column = columns.find(col => card.column_id === col.id);
      return {
        ...card,
        tags: card.tags || [] as KanbanCardTag[],
        column_name: column ? column.name : "Unknown",
      };
    });
  }, [cards, columns]);
  
  // Filter cards based on search query
  const filteredCards = useMemo(() => {
    if (!searchQuery.trim()) return cardsWithColumnNames;
    
    const query = searchQuery.toLowerCase();
    return cardsWithColumnNames.filter(card => 
      card.title.toLowerCase().includes(query) ||
      (card.description && card.description.toLowerCase().includes(query)) ||
      card.column_name.toLowerCase().includes(query) ||
      (card.tags && card.tags.some(tag => tag.name.toLowerCase().includes(query)))
    );
  }, [cardsWithColumnNames, searchQuery]);
  
  // Handle card delete
  const handleDeleteCard = async (cardId: string) => {
    try {
      await fetch(`/api/cards/${cardId}`, {
        method: "DELETE",
      });
      onRefreshCards();
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };
  
  // Handle opening the view dialog for a card
  const handleViewCard = (card: KanbanCard) => {
    setSelectedCard(card);
    setIsViewDialogOpen(true);
  };
  
  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4 flex justify-between items-center">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar cartões..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Coluna</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead>Data de Entrega</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCards.length > 0 ? (
                filteredCards.map((card) => (
                  <TableRow 
                    key={card.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => handleViewCard(card)}
                  >
                    <TableCell className="font-medium">{card.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{card.column_name}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {card.tags && card.tags.map((tag) => (
                          <Badge 
                            key={tag.id} 
                            style={{ 
                              backgroundColor: `${tag.color}20`, 
                              color: tag.color,
                              border: `1px solid ${tag.color}40`
                            }}
                          >
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{card.assignee || "-"}</TableCell>
                    <TableCell>
                      {card.due_date ? (
                        format(parseISO(card.due_date), "dd/MM/yyyy HH:mm", { locale: ptBR })
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            className="text-red-500 focus:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCard(card.id);
                            }}
                            onSelect={(e) => e.preventDefault()}
                          >
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    {searchQuery ? "Nenhum cartão encontrado com esses termos." : "Nenhum cartão disponível."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Card view dialog */}
        {selectedCard && (
          <CardDetailsDialog
            isOpen={isViewDialogOpen}
            setIsOpen={setIsViewDialogOpen}
            card={selectedCard}
            columnId={selectedCard.column_id}
            onCardUpdated={() => {
              onRefreshCards();
              setSelectedCard(null);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
